import {
  CHAMELEON_METHOD,
  OUTREACH_AGENT_METHOD_MAPPED_QUESTION_ID,
} from '../Data/OutreachAgentMethod';
import {
  getImplementationScreens,
  isSupportedImplementation,
  isUnknownComponent,
} from '../Data/ClientApiScreens';
import { ComponentTypes } from '../Data/Components';
import { buildPostSubmissionAnswers } from './postSubmissionAnswers.helpers';
import { getJourneySteps } from './strategyDag.helpers';

/** The post-submission method the form's hidden field declares, if any. */
export const getOutreachAgentMethod = (components) =>
  Object.values(components || {}).find(
    (component) =>
      component?.component?.mappedQuestionId ===
      OUTREACH_AGENT_METHOD_MAPPED_QUESTION_ID
  )?.component?.defaultValue ?? null;

/** Whether the form's hidden method field is set to Chameleon. */
export const isChameleonOutreachMethod = (components) =>
  getOutreachAgentMethod(components) === CHAMELEON_METHOD;

/** Whether a collection exists and its subscription runs the form's method. */
const hasCollectionForMethod = (collection, method) =>
  Boolean(method) && collection?.subscription?.postSubmissionMethod === method;

/**
 * The first match, in matcher order, whose subscription has a collection on the
 * form for the form's method. A supplier can hold one subscription per method
 * and the matcher returns an entry for each, so a higher match running another
 * method is passed over rather than ending the search.
 */
export const getMatchedStrategyCollection = ({
  customerMatches,
  bySubscriptionId,
  method,
}) => {
  const match = (customerMatches ?? []).find((candidate) =>
    hasCollectionForMethod(
      bySubscriptionId?.[candidate?.subscriptionId],
      method
    )
  );

  return match ? bySubscriptionId[match.subscriptionId] : null;
};

/** Whether any match holds a collection on the form, whatever method it runs. */
const hasAnyMatchedCollection = ({ customerMatches, bySubscriptionId }) =>
  (customerMatches ?? []).some((match) =>
    Boolean(bySubscriptionId?.[match?.subscriptionId])
  );

// Which way a matched collection's journey cannot be run here, or null when it
// can. Each reason is its own metric tag so Datadog says what to fix: the dag
// (dagCycle, missingImplementation, noChameleonJourney — [] from PHP as well as
// {}) or this widget's screen registry (unmappedStepType, also the fallback for
// anything else the walk throws).
const NOT_RUNNABLE_REASON_BY_ERROR = {
  DagCycleError: 'dagCycle',
  MissingImplementationError: 'missingImplementation',
};

export const getJourneyNotRunnableReason = (collection) => {
  try {
    const steps = getJourneySteps(collection);
    if (steps.length === 0) return 'noChameleonJourney';
    if (
      !steps.every(({ implementation }) =>
        isSupportedImplementation(implementation)
      )
    )
      return 'unmappedStepType';

    // Every step degrading to no screen leaves a blank journey, which is worse
    // than an ordinary lead, so do not offer it.
    const hasAnyScreen = steps.some(
      ({ implementation }) =>
        (getImplementationScreens(implementation) ?? []).length > 0
    );
    return hasAnyScreen ? null : 'unmappedStepType';
  } catch (error) {
    return NOT_RUNNABLE_REASON_BY_ERROR[error?.name] ?? 'unmappedStepType';
  }
};

/**
 * Whether the dag walks cleanly, has steps, every step resolves to screens
 * (an unsupported type with no component still drops the journey), and at least
 * one step actually has a screen.
 */
export const hasRunnableJourney = (collection) =>
  getJourneyNotRunnableReason(collection) === null;

/**
 * Whether any step named a component this build does not have, so its intended
 * screen was replaced by the type default or skipped. Call only on an offered
 * journey, whose dag already walked cleanly.
 */
export const hasUnknownComponentStep = (collection) =>
  getJourneySteps(collection).some(({ implementation }) =>
    isUnknownComponent(implementation)
  );

const selectMatchArgs = (state) => ({
  customerMatches: state.supplierMatches?.customerMatches,
  bySubscriptionId: state.strategyCollections?.bySubscriptionId,
  method: getOutreachAgentMethod(state.components),
});

export const selectMatchedStrategyCollection = (state) =>
  getMatchedStrategyCollection(selectMatchArgs(state));

/**
 * What gates the booking journey. A matched collection with no journey for us
 * ends it rather than passing the turn to a lower-value match.
 */
export const selectChameleonStrategyCollection = (state) => {
  if (!isChameleonOutreachMethod(state.components)) return null;

  const collection = selectMatchedStrategyCollection(state);

  return hasRunnableJourney(collection) ? collection : null;
};

const isComponentOfType = (components, type) => (componentId) =>
  components[`component_${componentId}`]?.component?.type === type;

/**
 * The radios sharing a page with the form's client-api component, in page
 * order. They are the booking journey's post-submission questions, so the page
 * never draws them itself: with a journey on offer the questions screen asks
 * them, and with none the page has nothing left to block the skip to the
 * thank-you page. Independent of any collection.
 */
export const getPostSubmissionQuestionComponentIds = ({ components, pages }) =>
  (pages ?? [])
    .filter((pageComponentIds) =>
      (pageComponentIds ?? []).some(
        isComponentOfType(components, ComponentTypes.CLIENT_API)
      )
    )
    .flatMap((pageComponentIds) =>
      pageComponentIds.filter(
        isComponentOfType(components, ComponentTypes.RADIOS)
      )
    );

/** getPostSubmissionQuestionComponentIds read from the store. */
export const selectPostSubmissionQuestionComponentIds = (state) =>
  getPostSubmissionQuestionComponentIds({
    components: state.components,
    pages: state.paging?.pages,
  });

/** The merged post-submission answers map, built from the store. */
export const selectPostSubmissionAnswers = (state) =>
  buildPostSubmissionAnswers({
    componentIds: selectPostSubmissionQuestionComponentIds(state),
    components: state.components,
    stepResponses: state.clientApiJourney?.stepResponses,
  });

/**
 * Why a form holding this component was offered no journey, for the cases
 * worth seeing: it asks for another method (methodNotChameleon), its matches
 * hold collections that all run another method (noCollectionForMethod), or its
 * collection matched but the journey cannot be run — see
 * getJourneyNotRunnableReason. Null for an ordinary lead that matched nothing,
 * which is not a misconfiguration.
 */
export const selectJourneyNotOfferedReason = (state) => {
  if (!isChameleonOutreachMethod(state.components)) {
    return 'methodNotChameleon';
  }

  const collection = selectMatchedStrategyCollection(state);

  if (collection) return getJourneyNotRunnableReason(collection);

  return hasAnyMatchedCollection(selectMatchArgs(state))
    ? 'noCollectionForMethod'
    : null;
};
